import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Leaf, Loader2, Crown, Mail } from 'lucide-react';
import { API, useAuth } from '../contexts/AuthContext';

export default function PremiumSuccess() {
  const { user, setUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading | done | error
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const confirm = async () => {
      try {
        await axios.post(`${API}/membership/confirm`, { app_url: window.location.origin });
        setUser((u) => (u ? { ...u, is_premium: true, plan: 'Premium' } : u));
        setStatus('done');
      } catch (e) {
        setStatus('error');
      }
    };
    confirm();
  }, [setUser]);

  return (
    <div className="max-w-lg mx-auto px-5 py-20 text-center min-h-[70vh]">
      {status === 'loading' && (
        <>
          <Loader2 className="w-10 h-10 text-botanica-green animate-spin mx-auto mb-5" />
          <p className="text-botanica-muted">Confirming your Premium membership…</p>
        </>
      )}

      {status === 'done' && (
        <div className="animate-fade-up">
          <span className="w-20 h-20 rounded-3xl bg-botanica-greenLight flex items-center justify-center mx-auto mb-6 relative">
            <CheckCircle2 className="w-10 h-10 text-botanica-green" />
            <Crown className="w-6 h-6 text-botanica-green absolute -top-2 -right-2" />
          </span>
          <h1 className="font-serif text-4xl text-botanica-ink mb-3">You're Premium! 🌿</h1>
          <p className="text-botanica-muted leading-relaxed mb-4">
            Your 7-day free trial has started. Enjoy unlimited Sage, personalised profiles,
            and advanced safety tools.
          </p>
          <p className="text-sm text-botanica-muted flex items-center justify-center gap-2 mb-8">
            <Mail className="w-4 h-4 text-botanica-green" />
            {user?.email ? `A confirmation email is on its way to ${user.email}.` : 'A confirmation email is on its way.'}
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/ask" className="px-6 py-3 rounded-full bg-botanica-green text-white font-medium hover:bg-botanica-greenDark transition-colors">
              Chat with Sage
            </Link>
            <Link to="/library" className="px-6 py-3 rounded-full border border-botanica-card bg-botanica-panel text-botanica-ink font-medium hover:bg-botanica-card transition-colors">
              Explore the library
            </Link>
          </div>
        </div>
      )}

      {status === 'error' && (
        <>
          <span className="w-16 h-16 rounded-2xl bg-botanica-greenLight flex items-center justify-center mx-auto mb-5">
            <Leaf className="w-7 h-7 text-botanica-green" />
          </span>
          <h1 className="font-serif text-3xl text-botanica-ink mb-2">Almost there</h1>
          <p className="text-botanica-muted mb-6">We couldn't confirm your membership. Please make sure you're signed in and try again.</p>
          <Link to="/membership" className="px-6 py-3 rounded-full bg-botanica-green text-white font-medium hover:bg-botanica-greenDark transition-colors">
            Back to membership
          </Link>
        </>
      )}
    </div>
  );
}
